import { Suspense, useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { ConnectionHandler, fetchQuery } from "relay-runtime"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormError } from "@/components/ui/form"
import { SearchInput, SuggestionSearch } from "@/components/ui/search"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useToast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { joinStrings } from "@/lib/joinString"
import { useHealthInformationCopilotContext } from "@/modules/patients/contexts/health-information-copilot-context"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { EmptyMedicationQuestion } from "@/modules/patients/detail/medication/components/empty-medication-question"
import { DeleteMedicationButton } from "@/modules/patients/detail/medication/de/delete-medication-button"
import { MedicationCreation } from "@/modules/patients/detail/medication/de/medication-creation"
import { MedicationViewer } from "@/modules/patients/detail/medication/de/medication-viewer"
import {
  DeMedicationField,
  MedicationSchema,
  MedicationSchemaInput,
  MedicationSchemaType,
} from "@/modules/patients/detail/medication/de/schema/medication-schema"
import {
  Medication,
  MedicationSearchResultsType,
  PrescriptionMedicationSearchResults,
  SearchResults,
  Substance,
} from "@/modules/patients/detail/medication/de/search-results"
import { deMedicationFragmentInline$data } from "@/modules/patients/detail/medication/de/services/__generated__/deMedicationFragmentInline.graphql"
import { dePreparationByNameQuery as dePreparationByNameQueryType } from "@/modules/patients/detail/medication/de/services/__generated__/dePreparationByNameQuery.graphql"
import { deVerifyMedicationQuery } from "@/modules/patients/detail/medication/de/services/__generated__/deVerifyMedicationQuery.graphql"
import { dePreparationByNameQuery } from "@/modules/patients/detail/medication/de/services/dePreparationByNameQuery"
import { useDeUpdateMedicationMutation } from "@/modules/patients/detail/medication/de/services/deUpdateMedicationMutation"
import { de_VerifyMedicationQuery } from "@/modules/patients/detail/medication/de/services/deVerifyMedicationQuery"
import { useDeCreateMedicationMutation } from "@/modules/patients/detail/medication/de/services/useDeCreateMedicationMutation"
import { useDeDeleteMedicationMutation } from "@/modules/patients/detail/medication/de/services/useDeDeleteMedicationMutation"
import { useGetMedicationVerification } from "@/modules/patients/detail/medication/hooks/useGetMedicationVerification"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { isFreeTextMedicationType } from "@/modules/patients/detail/medication/lib/is-free-text-medication-type"
import { useVerifyMedicationMutation } from "@/modules/patients/detail/medication/services/useVerifyMedicationMutation"
import { useDeMedicationContext } from "@/modules/patients/detail/medication/store/useDeMedicationStore"
import {
  MAX_MEDICATION_SEARCH_CHARACTERS,
  MIN_MEDICATION_SEARCH_CHARACTERS,
} from "@/modules/patients/detail/medication/types/medication-config"
import { DeMedicationFragmentInlineList } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { MEDICATION_MODES } from "@/modules/patients/detail/medication/types/medication-mode"
import { PRN_UNIT } from "@/modules/patients/detail/medication/types/medication-unit"
import {
  MEDICATION_VERIFICATION_STATUSES,
  NOT_ENQUIRED_STATUS,
} from "@/modules/patients/detail/medication/types/medication-verification-status"
import { DePrescriptionDrawer } from "@/modules/patients/detail/prescription/de/de-prescription-drawer"
import { useDePrescriptionStoreContext } from "@/modules/patients/detail/prescription/de/store/usePrescriptionStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useDeMedicationSearchQuery } from "@/modules/patients/services/de/__generated__/useDeMedicationSearchQuery.graphql"
import { deMedicationSearchQuery } from "@/modules/patients/services/de/useDeMedicationSearchQuery"
import { RelayEnvironment } from "@/relay-environment"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { DeMedicationType, VerifyMedicationStatus } from "@/types/__generated__/union-types"
import { ArrElement } from "@/types/ArrElement"

type DeMedicationDrawerProps = {
  prescriptionMedicationSearchResults?: PrescriptionMedicationSearchResults
  recommendedMedication?: MedicationSearchResultsType & {
    onAddSuccess: (id: string) => void
    progressNoteId: string
    editMedication?: deMedicationFragmentInline$data
  }
}

const defaultValues: MedicationSchemaType = {
  medication: null,
  substance: null,
  unit: "PC",
  morningDosage: "0",
  noonDosage: "0",
  eveningDosage: "0",
  nightDosage: "0",
  internalNote: "",
  usageInstruction: "",
  pharmaceuticalCentralNumber: "",
  freeTextMedicationName: "",
  numberOfPackages: 1,
  refuseSubstitutes: false,
}

const fetchVerifyMedicationData = (input: deVerifyMedicationQuery["variables"]) => {
  fetchQuery<deVerifyMedicationQuery>(RelayEnvironment, de_VerifyMedicationQuery, input).toPromise()
}

const fetchPreparationByProductName = (
  input: dePreparationByNameQueryType["variables"],
  next: (data: dePreparationByNameQueryType["response"]) => void,
) => {
  fetchQuery<dePreparationByNameQueryType>(RelayEnvironment, dePreparationByNameQuery, input).subscribe({ next })
}

const DeMedicationDrawer = ({
  prescriptionMedicationSearchResults,
  recommendedMedication,
}: DeMedicationDrawerProps) => {
  const user = useUser()
  const hasSavePermission = user.isOneOf(["Doctor", "MedicalSupervisor", "NursingProfessional"])
  const { t } = useTranslation()
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)
  const isAddingFreeTextMedicationRef = useRef(
    !!recommendedMedication?.freeTextMedicationName || !!prescriptionMedicationSearchResults?.freeTextMedicationName,
  )
  /*
   * recommendedMedicationRef is used to track data processing value from Recommended Medication
   */
  const recommendedMedicationRef = useRef(false)
  const [searchResultVisible, setSearchResultVisible] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [selectedPharma, setSelectedPharma] = useState<ArrElement<Medication["pharmaceuticalCentralNumberList"]>>()
  const deMedicationMode = useDeMedicationContext((state) => state.deMedicationMode)
  const chooseSearchItem = useDeMedicationContext((state) => state.chooseSearchItem)
  const setDeMedicationDrawerMode = useDeMedicationContext((state) => state.setDeMedicationDrawerMode)
  const resetDeMedicationStore = useDeMedicationContext((state) => state.reset)
  const selectExistingMedication = useDeMedicationContext((state) => state.selectExistingMedication)
  const existingMedication = useDeMedicationContext((state) => state.existingMedication)
  const setPharmaceuticalCentralNumberList = useDeMedicationContext((state) => state.setPharmaceuticalCentralNumberList)
  const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)
  const setUnSavedMedicationIds = useDePrescriptionStoreContext((state) => state.setUnSavedMedicationIds)
  const removeUnSavedMedicationId = useDePrescriptionStoreContext((state) => state.removeUnSavedMedicationId)
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const { toast } = useToast()
  const [deleteDeMedication] = useDeDeleteMedicationMutation()

  const selectedCopilotMedication = useHealthInformationCopilotContext((state) => state.medications.selected)
  const setSelectedCopilotMedication = useHealthInformationCopilotContext((state) => state.medications.setSelected)
  const resetSelectedCopilotMedication = useHealthInformationCopilotContext((state) => state.medications.resetSelected)

  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()
  const isEditMode = useEditMode()
  const { activeMedications, inactiveMedications } = useMedications<DeMedicationFragmentInlineList>()
  const { medicationVerificationData } = useGetMedicationVerification()
  const {
    lastVerifyMedicationStatus: verifyMedicationStatus,
    lastVerifiedAt: verifiedAt,
    lastVerifiedBy: verifiedBy,
  } = medicationVerificationData || {}
  const [verificationStatus, setVerificationStatus] = useState<VerifyMedicationStatus>(
    verifyMedicationStatus && MEDICATION_VERIFICATION_STATUSES.includes(verifyMedicationStatus)
      ? verifyMedicationStatus
      : NOT_ENQUIRED_STATUS,
  )
  const [data, setData] = useState<dePreparationByNameQueryType["response"] | null>(null)
  const [medicationData, setMedicationDataState] = useState<useDeMedicationSearchQuery["response"] | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, startTransition] = useTransition()

  const existingPreparationData = data?.de_preparationByProductName?.result

  const isMedicationEditMode = existingMedication?.id
  const isAddMode = !isMedicationEditMode && patientId

  const form = useForm<MedicationSchemaInput, unknown, MedicationSchemaType>({
    mode: "all",
    resolver: zodResolver(MedicationSchema),
    defaultValues: { ...defaultValues, freeTextMedicationName: recommendedMedication?.freeTextMedicationName ?? "" },
  })
  const [createMedication, isCreating] = useDeCreateMedicationMutation()
  const [updateMedication, isUpdating] = useDeUpdateMedicationMutation()
  const verifyMedicationMutation = useVerifyMedicationMutation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)

  const formattedPharmaceuticalCentralNumberList = useMemo(
    () =>
      activeMedications
        .flatMap((item) => item.pharmaceuticalCentralNumber ?? "")
        .filter((pharmaceuticalCentralNumber) => pharmaceuticalCentralNumber),
    [activeMedications],
  )

  const {
    setValue,
    reset,
    getValues,
    formState: { errors, dirtyFields },
  } = form
  const isDirty = Object.keys(dirtyFields).length > 0

  const handleSearchInputChange = (value: string) => {
    closeSearchResults()
    setSearchValue(value)
  }

  const performMedicationSearch = useCallback(
    async (searchTerm: string) => {
      if (searchTerm.trim().length >= MIN_MEDICATION_SEARCH_CHARACTERS) {
        startTransition(async () => {
          return new Promise((resolve, reject) => {
            fetchQuery<useDeMedicationSearchQuery>(RelayEnvironment, deMedicationSearchQuery, {
              searchTerm,
            }).subscribe({
              next: (data) => {
                setMedicationDataState(data)
                resolve()
              },
              error: (err: Error) => {
                setError(err)
                reject(err)
              },
            })
          })
        })
        setSearchResultVisible(true)
      }
    },
    [startTransition],
  )

  const handleSearchSubmit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && searchValue.trim().length >= MIN_MEDICATION_SEARCH_CHARACTERS) {
      performMedicationSearch(searchValue)
    }
  }

  const handleClearSearch = () => {
    setSearchValue("")
    closeSearchResults()
    searchRef.current?.focus()
  }

  const closeSearchResults = () => {
    setSearchResultVisible(false)
  }

  const prevDeMode = useDeMedicationContext((state) => state.prevDeMode)

  const redirectToPrescription = useCallback(() => {
    resetDeMedicationStore()
    openDrawerWithoutConfirmation(
      () => (
        <DePrescriptionDrawer
          progressNoteId={prescriptionMedicationSearchResults?.progressNoteId ?? ""}
          prescriptionRef={prescriptionMedicationSearchResults?.prescriptionRef}
          id={prescriptionMedicationSearchResults?.id}
        />
      ),
      "PrescriptionDrawer",
    )
  }, [prescriptionMedicationSearchResults, resetDeMedicationStore, openDrawerWithoutConfirmation])

  const handleDrawerChange = useCallback(() => {
    isAddingFreeTextMedicationRef.current = false
    if (prescriptionMedicationSearchResults) {
      redirectToPrescription()
      return
    }

    resetDeMedicationStore()

    if (prevDeMode === MEDICATION_MODES.VIEW && deMedicationMode !== MEDICATION_MODES.VIEW) {
      if (existingPreparationData) setData(null)
      setDeMedicationDrawerMode(MEDICATION_MODES.VIEW)
      FaroInsightsWrapper.pushEvent("healthInformation_de_medication_cancel")
      return
    }
    if (prevDeMode === MEDICATION_MODES.CREATION && deMedicationMode === MEDICATION_MODES.CREATION) {
      setDeMedicationDrawerMode(null)
    }

    closeDrawer()
    resetSelectedCopilotMedication()
    FaroInsightsWrapper.pushEvent("healthInformation_de_medication_close")
  }, [
    closeDrawer,
    deMedicationMode,
    existingPreparationData,
    prescriptionMedicationSearchResults,
    prevDeMode,
    redirectToPrescription,
    resetDeMedicationStore,
    setDeMedicationDrawerMode,
    resetSelectedCopilotMedication,
  ])

  const setMedicationData = useCallback(
    (medication: Medication) => {
      setValue("medication", medication, {
        shouldValidate: true,
        shouldDirty: true,
      })

      if (medication.pharmaceuticalCentralNumberList[0]) {
        const newSelectedPharma = { ...medication.pharmaceuticalCentralNumberList[0] }
        setSelectedPharma(newSelectedPharma)
        setValue("pharmaceuticalCentralNumber", newSelectedPharma.pharmaceuticalCentralNumber)
        setPharmaceuticalCentralNumberList([
          ...formattedPharmaceuticalCentralNumberList,
          newSelectedPharma.pharmaceuticalCentralNumber,
        ])
      }
    },
    [formattedPharmaceuticalCentralNumberList, setPharmaceuticalCentralNumberList, setValue],
  )

  const handleMedicationSelection = (medication: Medication) => {
    if (!searchResultVisible) return

    setSelectedPharma(undefined)

    setMedicationData(medication)

    resetMedicationFieldData("medication")
    chooseSearchItem()
  }

  const handleSubstanceSelection = (substance: Substance) => {
    if (!searchResultVisible) return

    setSelectedPharma(undefined)

    setValue("substance", substance, {
      shouldValidate: true,
      shouldDirty: true,
    })
    resetMedicationFieldData("substance")
    chooseSearchItem()
  }

  const handleFreeTextMedicationSelection = () => {
    if (!searchResultVisible) return

    isAddingFreeTextMedicationRef.current = true

    setSelectedPharma(undefined)

    setValue("freeTextMedicationName", searchValue, {
      shouldValidate: true,
      shouldDirty: true,
    })
    resetMedicationFieldData("freeTextMedicationName")
    chooseSearchItem()
  }

  const resetMedicationFieldData = (field: DeMedicationField) => {
    const medicationFields: DeMedicationField[] = ["medication", "substance", "freeTextMedicationName"]
    medicationFields.forEach((item) => {
      if (item !== field)
        setValue(item, null, {
          shouldValidate: true,
          shouldDirty: true,
        })
    })

    handleClearSearch()
  }

  const formatPharmaInformationText = (
    pharma: ArrElement<Medication["pharmaceuticalCentralNumberList"]> | undefined,
  ): string | null => {
    if (!pharma) return null
    return `${pharma.unit} (${pharma.quantity})`
  }

  const handleSubmit = ({
    medication,
    substance,
    morningDosage,
    noonDosage,
    eveningDosage,
    nightDosage,
    refuseSubstitutes,
    startDate,
    endDate,
    unit,
    internalNote,
    usageInstruction,
    freeTextMedicationName,
    numberOfPackages,
    asNeeded,
    isPermanentPrescription,
  }: MedicationSchemaType) => {
    const connectionID = ConnectionHandler.getConnectionID(patientId, "DePatientByIdConnection_de_medications")

    const isPreparation = !!medication

    const productId = medication?.productId ?? substance?.substanceId ?? null
    const name = medication?.name ?? substance?.nameHtml ?? freeTextMedicationName
    if (!name) return

    const isPrescriptionRequired = isPreparation ? medication.isPrescriptionRequired : false
    const pharmaceuticalCentralNumber = isAddingFreeTextMedicationRef.current
      ? null
      : (selectedPharma?.pharmaceuticalCentralNumber ?? `${productId ?? ""}`)
    const packageSize = isAddingFreeTextMedicationRef.current ? null : formatPharmaInformationText(selectedPharma)
    const composition = isPreparation && medication.composition.length > 0 ? medication.composition[0] : null
    let medicationType: DeMedicationType = "FREE_TEXT_MEDICATION"
    if (medication?.productId) medicationType = "PREPARATION"
    if (substance?.nameHtml) medicationType = "SUBSTANCE"

    const payload = {
      pharmaceuticalCentralNumber,
      name,
      composition,
      refuseSubstitutes: refuseSubstitutes,
      startDate: startDate ? new DateOnly(startDate).toString() : undefined,
      endDate: endDate ? new DateOnly(endDate).toString() : undefined,
      unit,
      internalNotes: internalNote,
      usageInstructions: usageInstruction,
      morningDosage: asNeeded ? 0 : Number(morningDosage),
      noonDosage: asNeeded ? 0 : Number(noonDosage),
      eveningDosage: asNeeded ? 0 : Number(eveningDosage),
      nightDosage: asNeeded ? 0 : Number(nightDosage),
      isPrescriptionRequired,
      packageSize,
      numberOfPackages,
      isPermanentPrescription: !!isPermanentPrescription,
    }

    if (isMedicationEditMode) {
      updateMedication({
        variables: {
          input: {
            id: existingMedication.id,
            pharmaceuticalCentralNumber: payload.pharmaceuticalCentralNumber,
            name: payload.name,
            composition: payload.composition,
            refuseSubstitutes: payload.refuseSubstitutes,
            startDate: payload.startDate,
            endDate: payload.endDate,
            unit: payload.unit,
            internalNotes: payload.internalNotes,
            usageInstructions: payload.usageInstructions,
            morningDosage: payload.morningDosage,
            noonDosage: payload.noonDosage,
            eveningDosage: payload.eveningDosage,
            nightDosage: payload.nightDosage,
            isPrescriptionRequired: payload.isPrescriptionRequired,
            packageSize: payload.packageSize,
            numberOfPackages: payload.numberOfPackages,
            isPermanentPrescription: payload.isPermanentPrescription,
          },
          connections: [connectionID],
        },
        onCompleted(response) {
          const id = response.de_updateMedication.de_Medication?.id
          if (id) {
            if (prescriptionMedicationSearchResults) {
              redirectToPrescription()
              return
            }

            handleDrawerChange()
          }
        },
      })
    }

    if (isAddMode) {
      createMedication({
        variables: {
          input: {
            patientId,
            pharmaceuticalCentralNumber: payload.pharmaceuticalCentralNumber,
            name: payload.name,
            composition: payload.composition,
            refuseSubstitutes: payload.refuseSubstitutes,
            startDate: payload.startDate,
            endDate: payload.endDate,
            unit: payload.unit,
            internalNotes: payload.internalNotes,
            usageInstructions: payload.usageInstructions,
            morningDosage: payload.morningDosage,
            noonDosage: payload.noonDosage,
            eveningDosage: payload.eveningDosage,
            nightDosage: payload.nightDosage,
            isPrescriptionRequired: payload.isPrescriptionRequired,
            type: medicationType,
            packageSize: payload.packageSize,
            numberOfPackages: payload.numberOfPackages,
            isPermanentPrescription: payload.isPermanentPrescription,
          },
          connections: [connectionID],
        },
        onCompleted(response) {
          const id = response.de_addMedication.de_Medication?.id
          if (id) {
            if (prescriptionMedicationSearchResults) {
              redirectToPrescription()
              setUnSavedMedicationIds(id)
              return
            }
            if (recommendedMedication) {
              recommendedMedication.onAddSuccess(id)
            }

            handleDrawerChange()
          }
        },
      })
    }
  }

  useEffect(() => {
    setSearchValue("")

    if (deMedicationMode === MEDICATION_MODES.VIEW) {
      setSelectedPharma(undefined)
      setValue("medication", null)
      reset(defaultValues)
    }

    if (deMedicationMode === MEDICATION_MODES.CREATION) {
      setTimeout(() => {
        searchRef.current?.focus({
          preventScroll: true,
        })
      }, 200)
    }
  }, [deMedicationMode, reset, setValue])

  useClickOutside((event: MouseEvent) => {
    if (searchResultRef.current && !searchResultRef.current.contains(event.target as HTMLElement)) {
      closeSearchResults()
    }
  }, searchRef)

  useEffect(() => {
    if (existingPreparationData && existingMedication?.type === "PREPARATION") {
      setValue("medication", existingPreparationData)
      const selectedPharma = existingPreparationData.pharmaceuticalCentralNumberList.find(
        (item) => item.pharmaceuticalCentralNumber === existingMedication?.pharmaceuticalCentralNumber,
      )
      if (selectedPharma) setSelectedPharma(selectedPharma)
    }
  }, [existingPreparationData, setValue, existingMedication])

  useEffect(() => {
    if (selectedCopilotMedication) {
      isAddingFreeTextMedicationRef.current = false
      resetDeMedicationStore()
      if (existingPreparationData) setData(null)

      setDeMedicationDrawerMode(MEDICATION_MODES.VIEW)
      setSearchValue(selectedCopilotMedication)

      performMedicationSearch(selectedCopilotMedication).then(() => {
        setSelectedCopilotMedication(null)
      })
    }
  }, [
    selectedCopilotMedication,
    setSelectedCopilotMedication,
    resetDeMedicationStore,
    existingPreparationData,
    setDeMedicationDrawerMode,
    performMedicationSearch,
  ])

  const handleSelectExistingMedication = useCallback(
    (medication: deMedicationFragmentInline$data) => {
      const {
        name,
        morningDosage,
        noonDosage,
        eveningDosage,
        nightDosage,
        startDate,
        endDate,
        unit,
        usageInstructions,
        internalNotes,
        refuseSubstitutes,
        type,
        pharmaceuticalCentralNumber,
        numberOfPackages,
        isPermanentPrescription,
      } = medication

      reset({
        morningDosage: morningDosage.toString(),
        noonDosage: noonDosage.toString(),
        eveningDosage: eveningDosage.toString(),
        nightDosage: nightDosage.toString(),
        startDate: startDate ? new Date(startDate) : undefined,
        endDate: endDate ? new Date(endDate) : undefined,
        unit,
        usageInstruction: usageInstructions ?? "",
        internalNote: internalNotes ?? "",
        refuseSubstitutes: refuseSubstitutes,
        freeTextMedicationName: isFreeTextMedicationType(type) ? name : "",
        numberOfPackages: numberOfPackages ?? 1,
        asNeeded: unit === PRN_UNIT,
        isPermanentPrescription: !!isPermanentPrescription,
      })

      if (type === "PREPARATION") {
        const preparationData = {
          preparationName: name,
          pharmaceuticalCentralNumber: pharmaceuticalCentralNumber ?? "",
          skip: !name,
        }
        fetchPreparationByProductName(preparationData, setData)

        const foundPharma = inactiveMedications
          .filter((m) => m.__typename === "de_Medication")
          .find((item) => item.pharmaceuticalCentralNumber === pharmaceuticalCentralNumber)
        if (foundPharma && pharmaceuticalCentralNumber)
          setPharmaceuticalCentralNumberList([...formattedPharmaceuticalCentralNumberList, pharmaceuticalCentralNumber])
        else setPharmaceuticalCentralNumberList([...formattedPharmaceuticalCentralNumberList])
      }

      if (type === "SUBSTANCE" && pharmaceuticalCentralNumber) {
        setValue("substance", {
          nameHtml: name,
          substanceId: Number(pharmaceuticalCentralNumber),
        })
      }

      if (isFreeTextMedicationType(type)) isAddingFreeTextMedicationRef.current = true

      selectExistingMedication(medication)
    },
    [
      formattedPharmaceuticalCentralNumberList,
      inactiveMedications,
      reset,
      selectExistingMedication,
      setPharmaceuticalCentralNumberList,
      setValue,
    ],
  )

  const handleVerifyMedication = () => {
    if (!appointmentId) return

    verifyMedicationMutation(
      {
        appointmentId,
        patientId,
        verifyMedicationStatus:
          activeMedications.length > 0 || inactiveMedications.length > 0 ? "SOME" : verificationStatus,
      },
      () => {
        fetchVerifyMedicationData({ appointmentId, patientId, skip: false })
        closeDrawer()
      },
    )
  }

  const onDelete = () => {
    if (!existingMedication?.id) return
    const connectionID = ConnectionHandler.getConnectionID(patientId, "DePatientByIdConnection_de_medications")
    deleteDeMedication({
      variables: {
        input: {
          id: existingMedication.id,
        },
        connections: [connectionID],
      },
      onCompleted() {
        toast({
          description: t("patient.medication.deleteMessage.success"),
        })
        removeUnSavedMedicationId(existingMedication.id)
        cancel()
        handleDrawerChange()
      },
    })
  }

  const handleDelete = () => {
    confirm({
      title: `${t("common.button.delete")} ${joinStrings([existingMedication?.name, existingMedication?.composition])}`,
      content: t("common.message.cannotUndoAction"),
      confirmIcon: TrashSimple,
      onConfirm: onDelete,
      confirmVariant: "danger",
      confirmText: t("common.button.delete"),
      cancelText: t("common.button.keep"),
    })
  }

  useEffect(() => {
    if (!prescriptionMedicationSearchResults) return

    const { preparation, substance, medication, freeTextMedicationName } = prescriptionMedicationSearchResults

    if (medication) handleSelectExistingMedication(medication)
    if (preparation) setMedicationData(preparation)
    if (substance) setValue("substance", substance, { shouldDirty: true })
    if (freeTextMedicationName) {
      setValue("freeTextMedicationName", freeTextMedicationName)
      isAddingFreeTextMedicationRef.current = true
    }
  }, [prescriptionMedicationSearchResults, setValue, setMedicationData, handleSelectExistingMedication])

  useEffect(() => {
    if (!recommendedMedication || isAddingFreeTextMedicationRef.current || recommendedMedicationRef.current) return
    const { preparation, substance, editMedication, freeTextMedicationName } = recommendedMedication

    if (editMedication) handleSelectExistingMedication(editMedication)
    if (preparation) setMedicationData(preparation)
    if (substance) setValue("substance", substance, { shouldDirty: true })
    if (freeTextMedicationName) {
      setValue("freeTextMedicationName", freeTextMedicationName)
      isAddingFreeTextMedicationRef.current = true
    }

    recommendedMedicationRef.current = true
  }, [handleSelectExistingMedication, recommendedMedication, setMedicationData, setValue])

  useDirtyDrawerMaker(isDirty, handleDrawerChange)

  const isSearchable =
    deMedicationMode !== MEDICATION_MODES.EDIT && isEditMode && !isAddingFreeTextMedicationRef.current

  useEffect(() => {
    if (isSearchable) {
      // using setTimeout to set focus manually and prevent flicking
      setTimeout(() => searchRef.current?.focus(), 150)
    }
  }, [isSearchable])

  return (
    <Drawer open onOpenChange={handleDrawerChange}>
      <FormProvider {...form}>
        <DrawerContent data-testid="de-medication-drawer-content">
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="gap-4 pr-4">
            <DrawerTitle
              key={deMedicationMode}
              className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {deMedicationMode === MEDICATION_MODES.CREATION && t("patient.medication.addMedication")}
              {(deMedicationMode === MEDICATION_MODES.VIEW || deMedicationMode === MEDICATION_MODES.EDIT) &&
                t("patient.medication.medication")}
            </DrawerTitle>
            {isSearchable && (
              <SuggestionSearch>
                <SearchInput
                  data-testid="search-input-medication"
                  ref={searchRef}
                  value={searchValue}
                  placeholder={t("patient.medication.searchPlaceHolder")}
                  autoComplete="off"
                  isLoading={isLoading}
                  error={!!errors.medication}
                  onClear={handleClearSearch}
                  onChange={({ target }) => handleSearchInputChange(target.value)}
                  onKeyDown={handleSearchSubmit}
                  maxLength={MAX_MEDICATION_SEARCH_CHARACTERS}
                  autoFocus={false}
                />
                {errors.medication && <FormError>{t(errors.medication.message || "")}</FormError>}
                {!isLoading && (
                  <SearchResults
                    ref={searchResultRef}
                    data-testid="medication-search-result"
                    isVisible={searchResultVisible}
                    isError={!!error}
                    results={medicationData}
                    searchValue={searchValue}
                    onMedicationSelection={handleMedicationSelection}
                    onSubstanceSelection={handleSubstanceSelection}
                    onFreeTextMedicationSelection={handleFreeTextMedicationSelection}
                  />
                )}
              </SuggestionSearch>
            )}
          </DrawerHeader>
          <DrawerBody className="py-0">
            {(deMedicationMode === MEDICATION_MODES.CREATION || deMedicationMode === MEDICATION_MODES.EDIT) && (
              <MedicationCreation
                key={deMedicationMode}
                selectedPharma={selectedPharma}
                pharmaceuticalCentralNumberList={getValues("medication")?.pharmaceuticalCentralNumberList}
                isAddingFreeTextMedication={isAddingFreeTextMedicationRef.current}
                onPharmaChange={setSelectedPharma}
              />
            )}
            {deMedicationMode === MEDICATION_MODES.VIEW && (
              <>
                {activeMedications.length === 0 && inactiveMedications.length === 0 && isEditMode && (
                  <EmptyMedicationQuestion
                    verificationStatus={verificationStatus}
                    setVerificationStatus={setVerificationStatus}
                  />
                )}
                {(activeMedications.length > 0 || inactiveMedications.length > 0) && (
                  <MedicationViewer
                    key={deMedicationMode}
                    activeMedicationList={activeMedications as deMedicationFragmentInline$data[]}
                    inactiveMedicationList={inactiveMedications as deMedicationFragmentInline$data[]}
                    handleSelectExistingMedication={handleSelectExistingMedication}
                  />
                )}
              </>
            )}
          </DrawerBody>
          <DrawerFooter>
            <div className="flex w-full flex-wrap justify-between gap-2">
              {deMedicationMode === MEDICATION_MODES.VIEW && verifiedAt && verifiedBy && (
                <LastModificationInfo
                  updatedAt={verifiedAt}
                  updatedBy={verifiedBy}
                  data-testid="medication-last-verified-info"
                  displayedText={t("common.confirmedOn")}
                />
              )}
              {isMedicationEditMode && deMedicationMode === MEDICATION_MODES.EDIT && (
                <div className="flex gap-1">
                  <LastModificationInfo
                    updatedAt={existingMedication?.updatedAt ?? existingMedication.createdAt}
                    updatedBy={existingMedication?.updatedBy ?? existingMedication.createdBy}
                    displayedText={existingMedication?.updatedAt ? t("common.updatedOn") : t("common.createdOn")}
                  />
                  {isEditMode && (
                    <Suspense fallback={null}>
                      <DeleteMedicationButton id={existingMedication.id} onClick={handleDelete} />
                    </Suspense>
                  )}
                </div>
              )}
              {(deMedicationMode === MEDICATION_MODES.CREATION || deMedicationMode === MEDICATION_MODES.EDIT) && (
                <div className="ml-auto flex gap-2">
                  <Button variant="secondary" onClick={handleDrawerChange}>
                    {t("common.button.cancel")}
                  </Button>
                  {isEditMode && (
                    <Button
                      isLoading={isCreating || isUpdating}
                      onClick={() => {
                        FaroInsightsWrapper.pushEvent("healthInformation_de_medication_save")
                        form.handleSubmit(handleSubmit)()
                      }}
                      disabled={!hasSavePermission || (!!isMedicationEditMode && !isDirty)}
                      data-testid="medication-save-button">
                      {t("common.button.save")}
                    </Button>
                  )}
                </div>
              )}
              {deMedicationMode === MEDICATION_MODES.VIEW && (
                <div className="ml-auto flex gap-2">
                  <Button variant="secondary" onClick={handleDrawerChange}>
                    {t("common.button.close")}
                  </Button>
                  {isEditMode && appointmentId && (
                    <Button
                      onClick={() => {
                        FaroInsightsWrapper.pushEvent("healthInformation_de_medication_verify")
                        handleVerifyMedication()
                      }}
                      disabled={!hasSavePermission}
                      data-testid="medication-verify-button">
                      {t("common.button.verify")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  )
}

export { DeMedicationDrawer }
